import type { VoiceControl } from '@karehero/llama/pwa/pages/ConversationalFormPage/ChatComposer';
import type { ChoiceStyle } from '@karehero/llama/pwa/pages/ConversationalFormPage/ConversationalFormPage';

import {
  AGE_BRACKET_ID,
  HAS_UNPAID_CARER_ID,
  HELP_TIME_ID,
  IS_DISABLED_ID,
  LOCATION_ID,
  SAVINGS_ID,
} from 'config/benefitsCalculatorV2Steps';

// Onboarding v4 is one conversation; each flow below is one way of running it,
// picked by id from the `onboarding-flow` flag. Beats are finished by id, never
// by position, so a flow can be reordered without stranding anyone.

/** Who they are here for, from the first question. */
export type Who = 'someone-else' | 'myself' | 'exploring';

/** Whether to ask every triage question, or only those not already answered. */
export type QuestionsMode = 'gaps' | 'all';

/** What the talk waits on before moving on: the facts read, or only the summary. */
export type TalkWait = 'facts' | 'summary';

/** `prompted` asks who it is for first; `open` is one line from Keni and the prompts. */
export type TalkOpening = 'prompted' | 'open';

/** What shows while they are read: `lines` in turn, or a `checklist` kept in the chat. */
export type ReadingStyle = 'lines' | 'checklist';

/** Consent to AI asked on a `screen` before the chat, or as Keni's first message in the `chat`. */
export type ConsentStyle = 'screen' | 'chat';

export const CONSENT_STYLES: Record<ConsentStyle, ConsentStyle> = {
  screen: 'screen',
  chat: 'chat',
};

export type OnboardingV4Beat =
  /** A screen saying what is about to happen. */
  | { kind: 'welcome'; id: 'welcome' }
  /** Consent to AI reading what they share; declining leaves for v2. */
  | { kind: 'consent'; id: 'ai-consent' }
  /** Who they are here for. Shapes every word after it. */
  | { kind: 'who'; id: 'who' }
  /** Their situation in their own words, read before the flow moves on. */
  | { kind: 'talk'; id: 'talk' }
  /** Triage questions, by care profile question id, in the order asked. */
  | {
      kind: 'questions';
      id: 'questions';
      questionIDs: string[];
      mode: QuestionsMode;
    }
  /** What we heard, read back in the chat to be confirmed or corrected; it can ask what they need help with where that was left out. */
  | { kind: 'heard'; id: 'heard'; asksHelpWith?: boolean }
  /** How we can help, from what we now know; it can ask whether they want it. */
  | { kind: 'help'; id: 'help'; asks?: boolean }
  /** What we would need from them, as a checklist ticked as it is found. */
  | { kind: 'need'; id: 'need' }
  /** What they have of it, sent in any order, with the checklist held in view. */
  | { kind: 'gather'; id: 'gather' }
  /** A number to reach them on, typed, or left for later. */
  | { kind: 'phone'; id: 'phone' }
  /** Where they want to start; booking a call offers that alone, or later. */
  | { kind: 'start'; id: 'start'; booksCall?: boolean };

export type OnboardingV4BeatID = OnboardingV4Beat['id'];

export type OnboardingV4FlowID =
  | 'talk-first'
  | 'talk-first-orb'
  | 'talk-first-every-question'
  | 'talk-first-no-questions'
  | 'questions-first'
  | 'talk-first-more-questions'
  | 'talk-first-live'
  | 'talk-first-live-full';

export interface OnboardingV4Flow {
  id: OnboardingV4FlowID;
  /** What the flow tests, for whoever reads the results. */
  description: string;
  beats: OnboardingV4Beat[];
  voiceControl: VoiceControl;
  choiceStyle: ChoiceStyle;
  /** How the places to start are offered. */
  startChoiceStyle: ChoiceStyle;
  /** How long what they said is waited on before the flow goes on without it. */
  readingWaitMs: number;
  talkWaitsFor: TalkWait;
  talkOpening: TalkOpening;
  readingStyle: ReadingStyle;
  /** Keni on the first screen, a pause rather than an ask. */
  isKeniShown: boolean;
  /** Where consent to AI is asked; set per session by withConsentStyle. */
  consentStyle: ConsentStyle;
  /** The details checked on a screen at the end; else it finishes in the chat, the screen kept to retry from. */
  checksDetails: boolean;
}

/** James's top four: together they cover 88% of the high-value benefits. */
const TRIAGE_QUESTIONS = [
  LOCATION_ID,
  AGE_BRACKET_ID,
  IS_DISABLED_ID,
  HELP_TIME_ID,
];

const READING_WAIT_MS = 45_000;

const SCREENS: OnboardingV4Beat[] = [
  { kind: 'welcome', id: 'welcome' },
  { kind: 'consent', id: 'ai-consent' },
];

/** The beats before anyone is asked about who is cared for, which every flow opens with. */
export const OPENING: OnboardingV4Beat[] = [
  ...SCREENS,
  { kind: 'who', id: 'who' },
];

const HEARD: OnboardingV4Beat = { kind: 'heard', id: 'heard' };

/** How we can help, and where they want to start. */
const OFFER: OnboardingV4Beat[] = [
  { kind: 'help', id: 'help' },
  { kind: 'start', id: 'start' },
];

const CLOSING: OnboardingV4Beat[] = [HEARD, ...OFFER];

const questions = (
  mode: QuestionsMode,
  questionIDs = TRIAGE_QUESTIONS,
): OnboardingV4Beat => ({
  kind: 'questions',
  id: 'questions',
  questionIDs,
  mode,
});

const TALK: OnboardingV4Beat = { kind: 'talk', id: 'talk' };

const TALK_FIRST: OnboardingV4Flow = {
  id: 'talk-first',
  description:
    'Talk first, then only the triage questions the voice note left unanswered.',
  beats: [...OPENING, TALK, questions('gaps'), ...CLOSING],
  voiceControl: 'button',
  choiceStyle: 'chips',
  startChoiceStyle: 'attached',
  readingWaitMs: READING_WAIT_MS,
  talkWaitsFor: 'facts',
  talkOpening: 'prompted',
  readingStyle: 'lines',
  isKeniShown: false,
  consentStyle: 'screen',
  checksDetails: true,
};

export const ONBOARDING_V4_FLOWS: Record<OnboardingV4FlowID, OnboardingV4Flow> =
  {
    'talk-first': TALK_FIRST,
    'talk-first-orb': {
      ...TALK_FIRST,
      id: 'talk-first-orb',
      description:
        "As talk-first, with v3's orb and answers as rows: does the big orb get more voice notes?",
      voiceControl: 'orb',
      choiceStyle: 'rows',
      startChoiceStyle: 'rows',
    },
    'talk-first-every-question': {
      ...TALK_FIRST,
      id: 'talk-first-every-question',
      description:
        'Talk first, then every triage question, whatever the voice note said.',
      beats: [...OPENING, TALK, questions('all'), ...CLOSING],
    },
    'talk-first-no-questions': {
      ...TALK_FIRST,
      id: 'talk-first-no-questions',
      description:
        'Talk first and no triage questions: the least friction, the least known.',
      beats: [...OPENING, TALK, ...CLOSING],
    },
    'questions-first': {
      ...TALK_FIRST,
      id: 'questions-first',
      description:
        'Every triage question before they talk: nothing waits on the reading.',
      beats: [...OPENING, questions('all'), TALK, ...CLOSING],
    },
    'talk-first-more-questions': {
      ...TALK_FIRST,
      id: 'talk-first-more-questions',
      description:
        'As talk-first, with unpaid carers and savings too, for means-tested help.',
      beats: [
        ...OPENING,
        TALK,
        questions('gaps', [
          ...TRIAGE_QUESTIONS,
          HAS_UNPAID_CARER_ID,
          SAVINGS_ID,
        ]),
        ...CLOSING,
      ],
    },
    'talk-first-live': {
      ...TALK_FIRST,
      id: 'talk-first-live',
      description:
        "Vez's 2.0 first release: an open first ask, read from the live transcript, then what we heard, the gap questions, their phone number and booking a call.",
      beats: [
        ...SCREENS,
        TALK,
        { kind: 'heard', id: 'heard', asksHelpWith: true },
        questions('gaps'),
        { kind: 'phone', id: 'phone' },
        { kind: 'start', id: 'start', booksCall: true },
      ],
      checksDetails: false,
      talkWaitsFor: 'summary',
      talkOpening: 'open',
      isKeniShown: false,
    },
    'talk-first-live-full': {
      ...TALK_FIRST,
      id: 'talk-first-live-full',
      description:
        "Vez's 2.0 with the middle she has parked for the first release: how we can help, what we'd need and sending it, then where to start and the details to check.",
      beats: [
        ...SCREENS,
        TALK,
        HEARD,
        questions('gaps'),
        { kind: 'help', id: 'help', asks: true },
        { kind: 'need', id: 'need' },
        { kind: 'gather', id: 'gather' },
        { kind: 'start', id: 'start' },
      ],
      talkWaitsFor: 'summary',
      talkOpening: 'open',
      isKeniShown: false,
    },
  };

/** The latest flow, which /v4/onboarding starts unless a flag names another. */
export const DEFAULT_ONBOARDING_V4_FLOW_ID: OnboardingV4FlowID =
  'talk-first-live';

export const isOnboardingV4FlowID = (id: unknown): id is OnboardingV4FlowID =>
  typeof id === 'string' && id in ONBOARDING_V4_FLOWS;

/** The flow with this id, or the default for an id that is not one. */
export const onboardingV4Flow = (id?: unknown): OnboardingV4Flow =>
  ONBOARDING_V4_FLOWS[
    isOnboardingV4FlowID(id) ? id : DEFAULT_ONBOARDING_V4_FLOW_ID
  ];

/** The flow with consent asked as given: in the chat, it is Keni's first message and no screens come before it. */
export const withConsentStyle = (
  flow: OnboardingV4Flow,
  consentStyle: ConsentStyle,
): OnboardingV4Flow =>
  consentStyle === 'chat'
    ? {
        ...flow,
        consentStyle,
        beats: flow.beats.filter((beat) => beat.kind !== 'welcome'),
      }
    : { ...flow, consentStyle };
